import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'

import { ContentFlow, ContentFlowElement } from '@/app/module/campaigns/types/common'
import WhatsappFlowContent from '@/app/module/common/whatsapp/convo/whatsapp-flow-content'
import { TWhatsappFlowContent, TWhatsappFlowElement } from '@/app/types'

type Props = {
  activePageId?: string
  flow: ContentFlow
}

const WaFlowPreview: React.FC<Props> = ({ activePageId, flow }) => {
  const content = React.useMemo(() => mapFlowToWhatsappFlowContent(flow), [flow])

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">Preview</Typography>
      </Box>
      <FlowPaper elevation={0}>
        <FlowContent>
          <WhatsappFlowContent activePageId={activePageId} content={content} showPageNavigation={false} showPageTitle />
        </FlowContent>
      </FlowPaper>
    </Stack>
  )
}

export const mapFlowElementToWhatsappFlowElement = (element: ContentFlowElement): TWhatsappFlowElement => ({
  datePicker: element.datePicker
    ? {
        maxDate: element.datePicker.maxDate,
        minDate: element.datePicker.minDate,
        question: element.datePicker.question,
        unavailableDates: element.datePicker.unavailableDates,
      }
    : undefined,
  footer: element.footer
    ? {
        label: element.footer.label,
      }
    : undefined,
  heading: element.heading,
  longText: element.longText,
  multiSelect: element.multiSelect
    ? {
        helpText: '',
        options: element.multiSelect.answers.map((answer) => ({
          id: answer.id,
          title: answer.value,
        })),
        question: element.multiSelect.question,
        required: element.multiSelect.required ?? false,
      }
    : undefined,
  questionId: element.questionId,
  shortText: element.shortText
    ? {
        ...element.shortText,
        type: 'text',
      }
    : undefined,
  singleSelect: element.singleSelect
    ? {
        helpText: '',
        options: element.singleSelect.answers.map((answer) => ({
          id: answer.id,
          title: answer.value,
        })),
        question: element.singleSelect.question,
        required: element.singleSelect.required ?? false,
      }
    : undefined,
  subheading: element.subheading,
})

const mapFlowToWhatsappFlowContent = (flow: ContentFlow): TWhatsappFlowContent => ({
  button: flow.button,
  message: flow.text,
  pages: (flow.pages || []).map((page, pageIndex, pages) => {
    const isLastPage = pageIndex === pages.length - 1
    const defaultFooterLabel = isLastPage ? 'Submit' : 'Continue'
    const { footerLabel, nonFooterElements } = page.elements.reduce<{
      footerLabel?: string
      nonFooterElements: TWhatsappFlowElement[]
    }>(
      (result, element) => {
        const mappedElement = mapFlowElementToWhatsappFlowElement(element)

        if (mappedElement.footer) {
          if (result.footerLabel === undefined) {
            const normalizedLabel = mappedElement.footer.label.trim()
            result.footerLabel = normalizedLabel || defaultFooterLabel
          }

          return result
        }

        result.nonFooterElements.push(mappedElement)

        return result
      },
      {
        footerLabel: undefined,
        nonFooterElements: [],
      },
    )

    return {
      elements: [
        ...nonFooterElements,
        {
          footer: {
            label: footerLabel || defaultFooterLabel,
          },
        },
      ],
      id: page.id,
      title: page.title,
    }
  }),
  subtitle: '',
})

const FlowPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(4),
  border: `1px solid ${theme.palette.divider}`,
  maxWidth: 420,
  margin: '0 auto',
  overflow: 'hidden',
  paddingBottom: theme.spacing(2),
}))

const FlowContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
}))

export default WaFlowPreview

export type { Props as WaFlowPreviewProps }
